import { environment } from '../../../environments/environment';

export enum LogLevel {
  DEBUG = 0,
  LOG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
}

interface LoggerConfig {
  minLevel: LogLevel;
  enableTimestamp: boolean;
  enableColors: boolean;
}

const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: environment.production ? LogLevel.WARN : LogLevel.DEBUG,
  enableTimestamp: true,
  enableColors: true,
};

const COLORS = {
  reset: '\x1b[0m',
  debug: '\x1b[36m', // Cyan
  log: '\x1b[32m', // Green
  info: '\x1b[34m', // Blue
  warn: '\x1b[33m', // Yellow
  error: '\x1b[31m', // Red
  context: '\x1b[35m', // Magenta
};

export class Logger {
  private static config: LoggerConfig = { ...DEFAULT_CONFIG };

  constructor(private readonly context: string) {}

  static setConfig(config: Partial<LoggerConfig>): void {
    Logger.config = { ...Logger.config, ...config };
  }

  static setMinLevel(level: LogLevel): void {
    Logger.config.minLevel = level;
  }

  debug(message: string, ...optionalParams: unknown[]): void {
    this.printMessage(LogLevel.DEBUG, 'DEBUG', message, optionalParams);
  }

  log(message: string, ...optionalParams: unknown[]): void {
    this.printMessage(LogLevel.LOG, 'LOG', message, optionalParams);
  }

  info(message: string, ...optionalParams: unknown[]): void {
    this.printMessage(LogLevel.INFO, 'INFO', message, optionalParams);
  }

  warn(message: string, ...optionalParams: unknown[]): void {
    this.printMessage(LogLevel.WARN, 'WARN', message, optionalParams);
  }

  error(message: string, ...optionalParams: unknown[]): void {
    this.printMessage(LogLevel.ERROR, 'ERROR', message, optionalParams);
  }

  private printMessage(
    level: LogLevel,
    levelName: string,
    message: string,
    optionalParams: unknown[]
  ): void {
    if (level < Logger.config.minLevel) {
      return;
    }

    const timestamp = Logger.config.enableTimestamp
      ? `[${new Date().toISOString()}]`
      : '';

    const contextStr = `[${this.context}]`;

    let formattedMessage: string;

    if (Logger.config.enableColors) {
      const levelColor = this.getLevelColor(levelName);
      formattedMessage = `${timestamp} ${levelColor}${levelName}${COLORS.reset} ${COLORS.context}${contextStr}${COLORS.reset} ${message}`;
    } else {
      formattedMessage = `${timestamp} ${levelName} ${contextStr} ${message}`;
    }

    const consoleMethod = this.getConsoleMethod(level);

    if (optionalParams.length > 0) {
      consoleMethod(formattedMessage, ...optionalParams);
    } else {
      consoleMethod(formattedMessage);
    }
  }

  private getLevelColor(levelName: string): string {
    switch (levelName) {
      case 'DEBUG':
        return COLORS.debug;
      case 'LOG':
        return COLORS.log;
      case 'INFO':
        return COLORS.info;
      case 'WARN':
        return COLORS.warn;
      case 'ERROR':
        return COLORS.error;
      default:
        return COLORS.reset;
    }
  }

  private getConsoleMethod(
    level: LogLevel
  ): (...args: unknown[]) => void {
    switch (level) {
      case LogLevel.DEBUG:
        return console.debug.bind(console);
      case LogLevel.LOG:
        return console.log.bind(console);
      case LogLevel.INFO:
        return console.info.bind(console);
      case LogLevel.WARN:
        return console.warn.bind(console);
      case LogLevel.ERROR:
        return console.error.bind(console);
      default:
        return console.log.bind(console);
    }
  }
}
